import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  EyeOff,
  MoreHorizontal,
  Plus,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import type {
  BulkCrmLeadsResult,
  CrmLeadListItem,
  CrmLeadRejectReason,
  CrmLeadStatus,
  CrmLeadStatusFilter,
} from '@ip/shared-types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { isRowNavHandled, skipRowNav } from '@/components/shared/rowNav';
import { useCrmApi } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { hasCompanyRole } from '@/lib/permissions';
import { t } from '@/i18n';
import { useSheetParam } from '@/hooks/useSheetParam';
import type { AppShellContext } from '@/components/layout/AppShell';
import { LeadDetailSheet } from './LeadDetailSheet';
import { HideLeadDialog } from './HideLeadDialog';
import { NewLeadDialog } from './NewLeadDialog';
import { leadCapabilities } from './leads/leadCapabilities';

const PAGE_SIZE = 20;
const ALL_STATUSES = 'all';
const ACTIVE_STATUSES = 'active';

const STATUS_STYLES: Record<CrmLeadStatus, string> = {
  new: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
  assigned: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  converted: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  spam: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
  rejected: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
};

interface LeadColumn {
  key: string;
  label: string;
  /** Proportional width class — see the layout note on <Table>. */
  width: string;
  /** Applied to the header label only (the actions column has no visible one). */
  className?: string;
}

/**
 * The columns described ONCE: the header row, the empty-state `colSpan` and
 * the skeleton cell count are all derived from this list, so adding a column
 * can no longer leave a stale hard-coded count behind.
 */
function leadColumns(): LeadColumn[] {
  return [
    { key: 'select', label: '', width: 'w-[4%]' },
    { key: 'received', label: t('crm.leads.columns.received'), width: 'w-[11%]' },
    { key: 'source', label: t('crm.leads.columns.source'), width: 'w-[13%]' },
    { key: 'contact', label: t('crm.leads.columns.contact'), width: 'w-[17%]' },
    { key: 'company', label: t('crm.leads.columns.company'), width: 'w-[13%]' },
    { key: 'message', label: t('crm.leads.columns.message'), width: 'w-[18%]' },
    { key: 'status', label: t('crm.leads.columns.status'), width: 'w-[10%]' },
    { key: 'account', label: t('crm.leads.columns.account'), width: 'w-[10%]' },
    { key: 'actions', label: t('common.actions'), width: 'w-[4%]', className: 'sr-only' },
  ];
}

function LeadStatusBadge({ status }: { status: CrmLeadStatus }) {
  return (
    <Badge variant="secondary" className={STATUS_STYLES[status]}>
      {t(`crm.leadStatus.${status}`)}
    </Badge>
  );
}

interface LeadRowProps {
  lead: CrmLeadListItem;
  selected: boolean;
  canDelete: boolean;
  onOpen: (id: string) => void;
  onToggle: (id: string) => void;
  onHide: (id: string) => void;
  onRestore: (id: string) => void;
  onDelete: (id: string) => void;
}

/**
 * One queue row. Memoised — and fed only STABLE callbacks by the queue —
 * because every row carries a dropdown subtree: without this, a keystroke in
 * the search box reconciled all 20 of them before the request even left.
 */
const LeadRow = memo(function LeadRow({
  lead,
  selected,
  canDelete,
  onOpen,
  onToggle,
  onHide,
  onRestore,
  onDelete,
}: LeadRowProps) {
  const contact =
    [lead.firstName, lead.lastName].filter(Boolean).join(' ') || lead.email || lead.phone || '—';
  const secondary = lead.email ?? lead.phone ?? '';
  const source = lead.formKey ? `${lead.sourceName} · ${lead.formKey}` : lead.sourceName;
  const message = lead.messagePreview ?? '—';
  const company = lead.companyName ?? '—';
  const account = lead.accountName ?? '—';
  // A converted lead can be neither hidden, restored nor deleted — without
  // this the ⋮ would open an empty panel.
  const actions = leadCapabilities(lead.status, canDelete);

  return (
    <TableRow
      className="cursor-pointer"
      onClick={(event) => {
        if (isRowNavHandled(event)) return;
        onOpen(lead.id);
      }}
    >
      <TableCell onClick={(event) => event.stopPropagation()}>
        <Checkbox checked={selected} onCheckedChange={() => onToggle(lead.id)} />
      </TableCell>
      <TableCell className="truncate text-sm">
        {dayjs(lead.receivedAt).format('DD.MM.YYYY HH:mm')}
      </TableCell>
      <TableCell className="truncate text-sm" title={source}>
        {lead.sourceName}
        {lead.formKey ? <span className="text-muted-foreground"> · {lead.formKey}</span> : null}
      </TableCell>
      <TableCell className="overflow-hidden text-sm">
        <div className="min-w-0">
          <div className="truncate" title={contact}>
            {contact}
          </div>
          <div className="text-muted-foreground truncate text-xs" title={secondary}>
            {secondary}
          </div>
        </div>
      </TableCell>
      <TableCell className="truncate text-sm" title={company}>
        {company}
      </TableCell>
      <TableCell className="text-muted-foreground truncate text-sm" title={message}>
        {message}
      </TableCell>
      <TableCell className="overflow-hidden">
        <LeadStatusBadge status={lead.status} />
        {actions.canRestore && lead.rejectedReasonCode ? (
          <div className="text-muted-foreground truncate text-xs">
            {t(`crm.leadRejectReason.${lead.rejectedReasonCode}`)}
          </div>
        ) : null}
      </TableCell>
      <TableCell className="truncate text-sm" title={account}>
        {account}
      </TableCell>
      {/* `skipRowNav` instead of stopPropagation: the native event must still
          reach `document` so Radix can dismiss another row's open menu. */}
      <TableCell onClick={skipRowNav}>
        {actions.hasActions ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">{t('common.actions')}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {actions.canHide ? (
                <DropdownMenuItem onSelect={() => onHide(lead.id)}>
                  <EyeOff className="mr-2 h-4 w-4" />
                  {t('crm.leads.hide')}…
                </DropdownMenuItem>
              ) : null}
              {actions.canRestore ? (
                <DropdownMenuItem onSelect={() => onRestore(lead.id)}>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  {t('crm.leads.restore')}
                </DropdownMenuItem>
              ) : null}
              {actions.canDelete ? (
                <DropdownMenuItem variant="destructive" onSelect={() => onDelete(lead.id)}>
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t('common.delete')}…
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </TableCell>
    </TableRow>
  );
});

/**
 * `skipped` covers everything the server would not touch — a converted lead,
 * but also an id that is already gone — so the message stays neutral.
 */
function reportSkipped(result: BulkCrmLeadsResult) {
  if (result.skipped > 0) {
    toast.info(t('crm.leads.skipped', { count: String(result.skipped) }));
  }
}

export function LeadQueue() {
  const { nip } = useOutletContext<AppShellContext>();
  const { listLeads, bulkRejectLeads, bulkDeleteLeads, bulkRestoreLeads } = useCrmApi();
  const { isSuperAdmin, permissions } = useAuth();
  // Hard delete is irreversible, so it stays hidden until we KNOW the caller
  // is a company admin (the backend enforces the same rule).
  const canDelete = isSuperAdmin || hasCompanyRole(permissions, nip, ['admin']);

  const [items, setItems] = useState<CrmLeadListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string>(ACTIVE_STATUSES);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [newOpen, setNewOpen] = useState(false);
  // The two confirmed flows work on a set of ids: one row (dropdown or the
  // open sheet) or the whole selection (bulk bar) — same dialogs either way.
  const [hideIds, setHideIds] = useState<string[] | null>(null);
  const [deleteIds, setDeleteIds] = useState<string[] | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Bumped whenever the queue changed a lead, so an open sheet re-reads it.
  const [refreshKey, setRefreshKey] = useState(0);
  // Deep link `?lead=<id>` (the notification bell, and a deal's "source
  // lead" link navigate here) plus in-page opens (row click).
  const { id: openLeadId, open: openLead, close: closeLead } = useSheetParam('lead');

  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    try {
      const result = await listLeads(nip, {
        search: search.trim() || undefined,
        status: status === ALL_STATUSES ? undefined : (status as CrmLeadStatusFilter),
        page,
        pageSize: PAGE_SIZE,
      });
      if (seq !== loadSeq.current) return;
      setItems(result.items);
      setTotal(result.total);
      setForbidden(false);
    } catch (error) {
      if (seq !== loadSeq.current) return;
      if ((error as { status?: number })?.status === 403) setForbidden(true);
      else toast.error(t('errors.loadingFailed'));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [nip, search, status, page, listLeads]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  // `load` changes identity on every keystroke (it closes over `search`), and
  // the row callbacks must stay stable for the memoised rows — so they refresh
  // through this ref instead of depending on `load` directly.
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  }, [load]);
  const reload = useCallback(() => {
    void loadRef.current();
  }, []);

  // A selection made on page 1 must not silently act on rows the user can no
  // longer see, so it is dropped whenever the result set changes underneath.
  useEffect(() => {
    setSelected(new Set());
  }, [page, status, search]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const columns = leadColumns();
  const allOnPageSelected = items.length > 0 && items.every((item) => selected.has(item.id));

  const toggleAllOnPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) items.forEach((item) => next.delete(item.id));
      else items.forEach((item) => next.add(item.id));
      return next;
    });
  };

  const toggleOne = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  /**
   * Every hide/delete/restore, whether it came from a row, the sheet or the
   * bulk bar, goes through the BULK endpoint — one row is just a one-element
   * batch. That keeps the outcome reporting (`affected` / `skipped`) identical
   * for one lead and for fifty, and leaves a single code path to reason about.
   *
   * Acted-on ids always leave the selection, otherwise the bulk bar keeps
   * offering actions on a row that is now hidden or gone.
   */
  const runOnIds = useCallback(
    async (
      ids: string[],
      call: (ids: string[]) => Promise<BulkCrmLeadsResult>,
      doneKey: 'hidden' | 'deleted' | 'restored',
      /** Runs with the other post-success updates so they batch together. */
      onSuccess?: () => void,
    ): Promise<void> => {
      if (ids.length === 0) return;
      try {
        const result = await call(ids);
        toast.success(
          ids.length === 1
            ? t(`crm.leads.${doneKey}`)
            : t(`crm.leads.${doneKey}Many`, { count: String(result.affected) }),
        );
        reportSkipped(result);
        setSelected((prev) => {
          const next = new Set(prev);
          ids.forEach((id) => next.delete(id));
          return next;
        });
        setHideIds(null);
        setDeleteIds(null);
        onSuccess?.();
        reload();
        setRefreshKey((value) => value + 1);
      } catch {
        toast.error(t('errors.saveFailed'));
      }
    },
    [reload],
  );

  const handleHide = useCallback(
    async (reasonCode: CrmLeadRejectReason, note: string) => {
      await runOnIds(
        hideIds ?? [],
        (ids) => bulkRejectLeads(nip, { ids, reasonCode, note: note || undefined }),
        'hidden',
      );
    },
    [runOnIds, hideIds, bulkRejectLeads, nip],
  );

  const handleDelete = useCallback(async () => {
    const ids = deleteIds ?? [];
    setDeleting(true);
    await runOnIds(ids, (batch) => bulkDeleteLeads(nip, { ids: batch }), 'deleted', () => {
      // Closing must land in the SAME update batch as the refresh bump: the
      // sheet would otherwise re-read a lead that no longer exists and greet
      // the user with a 404 toast right after a successful delete.
      if (openLeadId && ids.includes(openLeadId)) closeLead();
    });
    setDeleting(false);
  }, [runOnIds, deleteIds, bulkDeleteLeads, nip, openLeadId, closeLead]);

  const handleRestore = useCallback(
    (ids: string[]) => {
      void runOnIds(ids, (batch) => bulkRestoreLeads(nip, { ids: batch }), 'restored');
    },
    [runOnIds, bulkRestoreLeads, nip],
  );

  // Stable per-row callbacks — see the note on <LeadRow>.
  const hideOne = useCallback((id: string) => setHideIds([id]), []);
  const deleteOne = useCallback((id: string) => setDeleteIds([id]), []);
  const restoreOne = useCallback((id: string) => handleRestore([id]), [handleRestore]);

  if (forbidden) {
    return (
      <div className="flex h-full items-center justify-center py-16">
        <p className="text-muted-foreground text-sm">{t('errors.accessDenied')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(event) => {
            setPage(1);
            setSearch(event.target.value);
          }}
          placeholder={t('crm.leads.searchPlaceholder')}
          className="max-w-sm"
        />
        <Select
          value={status}
          onValueChange={(value) => {
            setPage(1);
            setStatus(value);
          }}
        >
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ACTIVE_STATUSES}>{t('crm.leads.activeStatuses')}</SelectItem>
            <SelectItem value={ALL_STATUSES}>{t('crm.leads.allStatuses')}</SelectItem>
            {(['new', 'assigned', 'converted', 'rejected', 'spam'] as CrmLeadStatus[]).map((value) => (
              <SelectItem key={value} value={value}>
                {t(`crm.leadStatus.${value}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ml-auto">
          <Button onClick={() => setNewOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('crm.leads.newLead')}
          </Button>
        </div>
      </div>

      {selected.size > 0 ? (
        <div className="bg-muted flex flex-wrap items-center gap-3 rounded-md px-3 py-2 text-sm">
          <span>{t('crm.leads.selected', { count: String(selected.size) })}</span>
          <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
            {t('crm.leads.clearSelection')}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setHideIds([...selected])}>
            <EyeOff className="mr-2 h-4 w-4" />
            {t('crm.leads.hide')}…
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleRestore([...selected])}>
            <RotateCcw className="mr-2 h-4 w-4" />
            {t('crm.leads.restore')}
          </Button>
          {canDelete ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive"
              onClick={() => setDeleteIds([...selected])}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t('common.delete')}…
            </Button>
          ) : null}
        </div>
      ) : null}

      <Card>
        <CardContent className="p-0">
          {/* Fixed layout + per-column widths: lead values are attacker-supplied
              strings (a 500-char unbroken "company name" used to stretch the
              page itself), so every cell truncates and carries a title. The
              widths are PROPORTIONAL and sum to 100%: absolute ones summed past
              the content width of a 1440px laptop (sidebar + padding leave
              ~1136px), which in fixed layout starved the auto-width message
              column down to nothing. `min-w` keeps the columns readable on a
              narrow screen — that is what the container's overflow-x is for. */}
          <Table className="table-fixed min-w-[64rem]">
            <TableHeader>
              <TableRow>
                {columns.map((column) => (
                  <TableHead key={column.key} className={column.width}>
                    {column.key === 'select' ? (
                      <Checkbox checked={allOnPageSelected} onCheckedChange={toggleAllOnPage} />
                    ) : (
                      <span className={column.className}>{column.label}</span>
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {columns.map((column) => (
                      <TableCell key={column.key}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="text-muted-foreground py-10 text-center">
                    {t('crm.leads.empty')}
                  </TableCell>
                </TableRow>
              ) : (
                items.map((lead) => (
                  <LeadRow
                    key={lead.id}
                    lead={lead}
                    selected={selected.has(lead.id)}
                    canDelete={canDelete}
                    onOpen={openLead}
                    onToggle={toggleOne}
                    onHide={hideOne}
                    onRestore={restoreOne}
                    onDelete={deleteOne}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-1">
        <Button variant="ghost" size="icon" disabled={page === 1} onClick={() => setPage(1)}>
          <ChevronsLeft className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" disabled={page === 1} onClick={() => setPage(page - 1)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-muted-foreground px-2 text-sm">
          {page} / {totalPages}
        </span>
        <Button
          variant="ghost"
          size="icon"
          disabled={page >= totalPages}
          onClick={() => setPage(page + 1)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          disabled={page >= totalPages}
          onClick={() => setPage(totalPages)}
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </div>

      {/* The `key` is the dialog's reset: a fresh open remounts it, so a reason
          picked for one lead is never carried into the next. */}
      <HideLeadDialog
        key={hideIds ? hideIds.join(',') : 'closed'}
        open={hideIds !== null}
        count={hideIds?.length ?? 0}
        onOpenChange={(open) => !open && setHideIds(null)}
        onSubmit={handleHide}
      />

      <AlertDialog
        open={deleteIds !== null}
        onOpenChange={(open) => {
          if (!open && !deleting) setDeleteIds(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {(deleteIds?.length ?? 0) > 1
                ? t('crm.leads.deleteDialog.titleMany', { count: String(deleteIds?.length ?? 0) })
                : t('crm.leads.deleteDialog.title')}
            </AlertDialogTitle>
            <AlertDialogDescription>{t('crm.leads.deleteDialog.description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={(event) => {
                event.preventDefault();
                handleDelete();
              }}
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <LeadDetailSheet
        nip={nip}
        leadId={openLeadId}
        canDelete={canDelete}
        refreshKey={refreshKey}
        onClose={closeLead}
        onChanged={reload}
        onHide={hideOne}
        onRestore={restoreOne}
        onDelete={deleteOne}
      />
      <NewLeadDialog
        nip={nip}
        open={newOpen}
        onOpenChange={setNewOpen}
        onCreated={() => {
          setNewOpen(false);
          reload();
        }}
      />
    </div>
  );
}
